import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { NzModalModule } from 'ng-zorro-antd/modal';

import { routes } from './app.routes';
import { provideNzIcons } from 'ng-zorro-antd/icon';
import {
  ArrowLeftOutline,
  CloudUploadOutline,
  DownloadOutline,
  FileExcelOutline,
  GlobalOutline,
  LockOutline,
  PlayCircleOutline,
  PlusOutline,
  ReloadOutline,
  SafetyCertificateOutline,
  TeamOutline,
  UndoOutline,
  UserOutline,
  VideoCameraOutline,
} from '@ant-design/icons-angular/icons';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import {
  credencialesInterceptor,
  proveerSesion,
  sesionExpiradaInterceptor,
} from '@intelishs-group/session-core';
import { NotifyService } from '@intelishs-group/notify-service';
import { proveerI18n, proveerOverlays } from '@intelishs-group/ui';

export const appConfig: ApplicationConfig = {
  providers: [
    // ⚠️ Desde Angular 21 una app SIN este provider arranca zoneless. La migracion
    // de 20 → 21 lo agrega para conservar el comportamiento con zone.js que la
    // app tenia; quitarlo es pasar a zoneless, y eso es un cambio aparte que hay
    // que probar pantalla por pantalla, no un efecto de subir de version.
    //
    // La migracion lo pone en `main.ts` esparciendo `appConfig`; aqui se mueve a
    // `app.config.ts` para que todos los providers vivan en un solo sitio, igual
    // que en fbrokers.
    provideZoneChangeDetection(),
    // withComponentInputBinding: `videoteca/:idVideo` se enlaza al input del componente.
    provideRouter(routes, withComponentInputBinding()),
    provideBrowserGlobalErrorListeners(),
    // `credencialesInterceptor` es el de la librería (session-core >= 5.2.14).
    // Sustituye a la copia local, que dejaba intactas las URLs absolutas hacia
    // otros hosts: las prefirmadas de S3 —subir y reproducir video— fallan el
    // preflight CORS si el navegador manda `withCredentials`, porque S3 no
    // responde `Access-Control-Allow-Credentials`. Ese caso lo cubre ahora
    // `hostsSinCredenciales: ['amazonaws.com']` en `proveerSesion`, abajo.
    //
    // `sesionExpiradaInterceptor` sí es NUEVO: este repo no tenía ningún manejo
    // de 401, así que una sesión caída se veía como peticiones que fallaban sin
    // explicación.
    provideHttpClient(withInterceptors([credencialesInterceptor, sesionExpiradaInterceptor])),
    provideAnimationsAsync(),
    // `NzModalService` NO es `providedIn: 'root'`: lo provee `NzModalModule`. Aqui
    // nadie lo proveia — llegaba DE REBOTE del `<intelishs-file-viewer>` que
    // `app.html` monta en la raiz, porque ese componente importa el modulo en su
    // propio `imports` y eso iza el provider al injector de entorno.
    //
    // Funciona hasta que alguien quita el visor de `app.html`, y entonces se cae
    // algo que no tiene nada que ver con ese cambio. A `ffacturacion` le paso
    // exactamente eso al borrar su `modal-pdf-viewer` local.
    importProvidersFrom(NzModalModule),
    // Los overlays del CDK vuelven al apilamiento por z-index (ui >= 5.2.7). Sin
    // esto, desde Angular 21 un modal abierto tapa los avisos de SweetAlert y el
    // velo de carga: salen detras, no se pueden pulsar y la pantalla parece colgada.
    proveerOverlays(),
    // Idioma de la plataforma (ui >= 5.2.10): locale de Angular, LOCALE_ID y el
    // i18n de ng-zorro salen de UN valor, 'es-MX', y no pueden desalinearse.
    proveerI18n(),
    /**
     * ng-zorro sólo dibuja iconos que estén en el inyector. Por defecto registra
     * únicamente los que usan sus propios componentes (flechas, check, search…),
     * así que todo `nzType` propio de esta app tiene que declararse aquí o queda
     * un hueco: sin error, sin warning y sin ninguna señal en consola.
     */
    provideNzIcons([
      ArrowLeftOutline,
      CloudUploadOutline,
      DownloadOutline,
      FileExcelOutline,
      GlobalOutline,
      LockOutline,
      PlayCircleOutline,
      PlusOutline,
      ReloadOutline,
      SafetyCertificateOutline,
      TeamOutline,
      UndoOutline,
      UserOutline,
      VideoCameraOutline,
    ]),
    // Sustituye a `provideSession({ tokenName: 'sinay-token' })`, que sólo
    // guardaba un JSON en `sessionStorage` y dejaba que cada aplicación
    // resolviera por su cuenta quién estaba dentro. Ahora la sesión la resuelve
    // identidad, llega en cookie y se carga ANTES de montar el primer
    // componente.
    //
    // Con esto se cae `LAYOUT_ENV` entero: su `systemName` pasa a ser la `marca`
    // de `App`, y ni `apiUrl` ni `logoutUrl` le hacen falta al marco nuevo.
    proveerSesion({
      identidadUrl: environment.identidadUrl,
      // Se llama `logoutUrl` por historia —de cuando el único camino era cerrar
      // la sesión del SSO— pero es el login CENTRAL, que es a donde se va a
      // INICIAR sesión.
      loginUrl: environment.logoutUrl,
      // El aviso antes de mandar al login. Es obligatorio en la libreria y no por
      // gusto: su primera version redirigia en silencio y se comio este aviso,
      // que llevaba años en produccion.
      //
      // El `inject()` aqui FUNCIONA desde 5.0.5. Antes no: esta flecha corre
      // despues del `await` del inicializador, fuera del contexto de inyeccion,
      // asi que lanzaba NG0203 y la libreria se lo tragaba —el aviso no salia y
      // nada lo delataba—. Por eso habia un `SESION_CONFIG` con `useFactory`
      // duplicando esta configuracion justo debajo. Ya no hace falta: el servicio
      // invoca el aviso con `runInInjectionContext`.
      avisarSesionCaducada: () => inject(NotifyService).unauthorizedNotify(),
      // Sin `rutaSinPermiso`: quien no tiene el modulo va a `/`, y `inicioGuard` lo lleva a SU
      // primera pantalla.
      // Terceros a los que NO se manda la cookie: las URLs prefirmadas de S3
      // (`POST /UploadUrl` y `GET /{id}/PlayUrl` devuelven una de
      // `<bucket>.s3.amazonaws.com`). Con credenciales, su preflight falla.
      hostsSinCredenciales: ['amazonaws.com'],
    }),
    // ⚠️ Aquí había tres providers —`CLIENTE_CONFIG`, `COMPANIES_CONFIG` y
    // `CATALOG_CONFIG`— y esta aplicación no monta un solo componente de esas
    // tres librerías: su único rastro en todo `src/` era este archivo.
    //
    // Vienen del boilerplate, que los estampa en cada proyecto generado. Un
    // provider para una librería que no se usa no rompe nada —por eso llevaban
    // tanto— pero no es inofensivo: obliga a mantener tres dependencias y tres
    // bloques de configuración apuntando al backend, y le dice al que lee este
    // archivo que la aplicación habla con unos servicios que nunca llama.
  ],
};
